type Option<T> = {
  label: React.ReactNode;
  value: T;
  // The plain-string form of `label`, required only when `label` is rich (not a
  // string): what a search input shows once the option is picked, and what
  // local filtering matches against.
  labelText?: string;
};

type FieldBaseProps = {
  label?: React.ReactNode;
  name: string;
  disabled?: boolean;
  error?: React.ReactNode;
  fullWidth?: boolean;
  placeholder?: string;
  onBlur?: () => void;
};

type AsyncOptionsProps = {
  fetchError?: string;
  loading?: boolean;
  onRetry?: () => void;
};

type RemoteSearchProps = {
  searchDebounceMs?: number;
  searching?: boolean;
  onSearchChange?: (query: string) => void;
};

const getOptionText = <T>(option: Option<T>): string =>
  option.labelText ??
  (typeof option.label === "string" ? option.label : String(option.value));

const filterOptionsByQuery = <T>(
  options: Option<T>[],
  query: string,
): Option<T>[] => {
  const normalizedQuery = query.trim().toLowerCase();
  return options
    .map((option) => ({
      option,
      position: getOptionText(option).toLowerCase().indexOf(normalizedQuery),
    }))
    .filter(({ position }) => position !== -1)
    .sort((a, b) => a.position - b.position)
    .map(({ option }) => option);
};

export {
  filterOptionsByQuery,
  getOptionText,
  type AsyncOptionsProps,
  type FieldBaseProps,
  type Option,
  type RemoteSearchProps,
};
